import React, { createContext, useContext } from 'react';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';

import type { INavigateContext, NavigateProviderProps, RouteMapBase } from './interface';
import { openUrl, useNavigate } from './utils';

export const NavigateContext = createContext<INavigateContext<any>>(
  null as unknown as INavigateContext<any>,
);

export function useNavigateContext<RouteMap = unknown>() {
  return useContext<INavigateContext<RouteMap>>(NavigateContext);
}

export function NavigateProvider<RouteMap extends RouteMapBase>({
  routes,
  children,
}: NavigateProviderProps<RouteMap>) {
  const getRouteUrl = (name: string, params?: object): string => {
    let routeUrl = routes[name].url;
    let urlParams = '';
    if (params) {
      const pathParamMatch = [...routeUrl.matchAll(/\/:(\w+)/g)];
      const pathParamKeys = pathParamMatch.map((match) => match[1]);
      if (!isEmpty(pathParamKeys)) {
        routeUrl = routeUrl.replace(/\/:(\w+)/g, (match, paramKey: string) => {
          const paramValue = (params as Record<string, string>)[paramKey];
          if (!paramValue) {
            return match;
          }
          return `/${paramValue}`;
        });
      }

      // Exclude path params from search params
      const searchParamKeys = Object.keys(params).filter((key) => !pathParamKeys.includes(key));
      const searchParams = pick(params, searchParamKeys);
      if (!isEmpty(searchParams)) {
        urlParams = new URLSearchParams(searchParams as Record<string, string>).toString();
        urlParams = urlParams ? `?${urlParams}` : '';
      }
    }

    return `${routeUrl}${urlParams}`;
  };
  const navigate = useNavigate(getRouteUrl);

  return (
    <NavigateContext.Provider value={{ getRouteUrl, navigate, openUrl }}>
      {children}
    </NavigateContext.Provider>
  );
}
